import React, { useState } from "react";
import { Box, Button, Chip, Collapse, createStyles, Dialog, DialogContent, DialogTitle, Divider, IconButton, makeStyles, Paper, Snackbar, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography, withStyles } from "@material-ui/core";
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import { ExecutionsProjection, SchedulerProjection, ScheduleService } from "../../../../../services/ScheduleService";
import { discoveryApiRef, fetchApiRef, microsoftAuthApiRef, useApi } from '@backstage/core-plugin-api';
import useAsync from "react-use/esm/useAsync";
import cronstrue from 'cronstrue';
import Alert from "@material-ui/lab/Alert";

const StyledTableHeadCell = withStyles(() => ({
    head: {
        backgroundColor: '#6d6d6d',
        color: 'white'
    },
}))(TableCell);

const useStyles = makeStyles((theme) =>
    createStyles({
        executionsTable: {
            maxHeight: 200,
            '& *[class^="MuiTableRow-root"]': {
              backgroundColor: 'white',
            },
        },
        logsDialog: {
            padding: theme.spacing(2),
            marginBottom: theme.spacing(2),
            backgroundColor: 'rgb(231 228 228)',
            whiteSpace: 'break-spaces',
            maxHeight: 300,
            overflowY: 'auto'
        },
        executionDataBox: {
            flex: 1,
            display: 'flex',
            columnGap: 20,
            '& pre': {
                margin: 0,
                padding: 20,
                backgroundColor: 'rgb(231 228 228)'
            }
        },
        dataCode: {
            width: '100%',
            marginTop: theme.spacing(1),
            '& *[class^="MuiInputBase-root"]': {
                marginTop: theme.spacing(1)
            },
            '& textarea': {
                fontSize: '0.85rem',
                color: 'black',
                maxHeight: '100px',
                whiteSpace: 'break-spaces',
                overflowY: 'auto !important',
            }
        },
        titleDivider: {
            marginBottom: theme.spacing(2)
        }
    })
);

const LogsDialog = (props: {open: boolean, execution: ExecutionsProjection, onClose: () => void;}) => {
    const { onClose, execution, open } = props;
    const classes = useStyles();

    const handleClose = () => {
      onClose();
    };

    return (
      <Dialog onClose={handleClose} open={open} maxWidth="lg">
        <DialogTitle>Execution Logs</DialogTitle>
        <DialogContent>
            <Typography variant="h6">
                Logs
            </Typography>
            <Box className={classes.logsDialog}>
                <code>{execution.logs}</code>
            </Box>
            <Typography variant="h6">
                Error Message
            </Typography>
            <Box className={classes.logsDialog}>
                <code>{execution.errorMessage}</code>
            </Box>
        </DialogContent>
      </Dialog>
    );
  }

const ActiveDeactiveDialog = (props: {open: boolean, environment: string, scheduler: SchedulerProjection, onClose: (shouldSearch: boolean) => void;}) => {
    const { onClose, environment, scheduler, open } = props;
    const fetchApi = useApi(fetchApiRef);
    const discoveryApi = useApi(discoveryApiRef);
    const authApi = useApi(microsoftAuthApiRef);

    const [reason, setReason] = useState('');
    const [openSuccessAlert, setOpenSuccessAlert] = useState(false);
    const [openErrorAlert, setOpenErrorAlert] = useState(false);

    const isDisabled = scheduler.state === 'DISABLED';

    const handleActivate = async () => {
        const url = await discoveryApi.getBaseUrl('proxy');
        const scheduleService = new ScheduleService(url, fetchApi, environment, authApi);
        if(isDisabled)
            await scheduleService.enableSchedule(scheduler.id, reason)
                .then(() => setOpenSuccessAlert(true))
                .catch(() => setOpenErrorAlert(true));
        else
            await scheduleService.disableSchedule(scheduler.id, reason)
                .then(() => setOpenSuccessAlert(true))
                .catch(() => setOpenErrorAlert(true));
        setReason('');
        onClose(true);
    };

    return (<>
        <Dialog onClose={() => onClose(false)} open={open} maxWidth="lg">
            <DialogTitle>{isDisabled ? 'Enable' : 'Disable'} schedule</DialogTitle>
            <DialogContent>
                <TextField fullWidth multiline required
                    label="Reason"
                    minRows={4}
                    variant="outlined"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                />
                <Box display="flex" justifyContent="flex-end" width={500} marginTop={1} marginBottom={2}>
                    <Button onClick={() => onClose(false)} color="primary">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleActivate}
                        color="primary"
                        variant="contained"
                        style={{ marginLeft: 8 }}
                        disabled={!reason}>
                        { isDisabled ? 'Enable' : 'Disable' }
                    </Button>
                </Box>
            </DialogContent>
        </Dialog>

        <Snackbar open={openSuccessAlert} autoHideDuration={3000} onClose={() => setOpenSuccessAlert(false)}>
            <Alert onClose={() => setOpenSuccessAlert(false)} severity="success">
                Schedule changed successfully
            </Alert>
        </Snackbar>
        <Snackbar open={openErrorAlert} autoHideDuration={3000} onClose={() => setOpenErrorAlert(false)}>
            <Alert onClose={() => setOpenErrorAlert(false)} severity="error">
                Error changing Schedule
            </Alert>
        </Snackbar>
    </>);
};

export const ScheduleExecutionsRow = ( props: { row: SchedulerProjection, environment: string, handleUpdateRows: () => void; }) => {
    const { row, environment, handleUpdateRows } = props;
    const classes = useStyles();
    const fetchApi = useApi(fetchApiRef);
    const discoveryApi = useApi(discoveryApiRef);
    const authApi = useApi(microsoftAuthApiRef);

    const [openRow, setOpenRow] = useState(false);

    const [openLogsDialog, setOpenLogsDialog] = useState(false);
    const [openActiveDeactiveDialog, setOpenActiveDeactiveDialog] = useState(false);
    const [selectedExecution, setSelectedExecution] = useState<ExecutionsProjection>();

    const isProduction = environment === 'production';

    const handleOpenLogsDialog = (execution: ExecutionsProjection) => {
        setSelectedExecution(execution);
        setOpenLogsDialog(true);
    }

    const handleCloseActiveDeactiveDialog = (shouldSearch: boolean) => {
        setOpenActiveDeactiveDialog(false);
        if(shouldSearch) {
            setOpenRow(false);
            handleUpdateRows();
        }
    }

    const executions = useAsync(async () => {
        if(openRow) {
            const url = await discoveryApi.getBaseUrl('proxy');
            const scheduleService = new ScheduleService(url, fetchApi, environment, authApi);
            return await scheduleService.listExecutions(row.id);
        }
        return [];
    }, [openRow, discoveryApi]).value;

    const formatDate = (date: string) => {
        return date ? new Date(date).toLocaleString() : undefined;
    }

    return (<>
        <TableRow key={row.id}>
            <TableCell>
                <IconButton size="small" onClick={() => setOpenRow(!openRow)}>
                    {openRow ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                </IconButton>
            </TableCell>
            <TableCell component="th" scope="row">{row.id}</TableCell>
            <TableCell>{row.taskCode}</TableCell>
            <TableCell><Chip color="secondary" label={row.state} /></TableCell>
            <TableCell>{cronstrue.toString(row.recurrency, { locale: 'pt_BR' })}</TableCell>
            <TableCell>{formatDate(row.initialPeriod)}</TableCell>
            <TableCell>{formatDate(row.finalPeriod)}</TableCell>
            <TableCell align="center">{row.executions}</TableCell>
        </TableRow>

        {executions && (
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={12}>
                    <Collapse in={openRow} timeout="auto" unmountOnExit>
                        <Box padding={3}>
                            <Box display="flex" flexDirection="row" gridColumnGap={20} marginBottom={1}>
                                <Box flexGrow="1">
                                    <Typography variant="h6">Details</Typography>
                                    <Divider className={classes.titleDivider}/>
                                </Box>
                                <Button
                                    variant="contained" color="primary"
                                    onClick={() => setOpenActiveDeactiveDialog(true)}
                                    disabled={row.state === 'FINISHED' || isProduction}
                                >
                                    {row.state === 'DISABLED' ? 'Enable' : 'Disable'} schedule
                                </Button>
                            </Box>
                            <Box display="flex">
                                <Typography style={{ flexGrow: 1 }}>
                                    <strong>Details:</strong>
                                        {row.details && row.details.split('\n').map((line) => (
                                            <><br/>{line}</>
                                        ))} <br/>
                                    <strong>Task Code:</strong> {row.taskCode} <br/>
                                    <strong>Description:</strong> {row.taskDescription} <br/>
                                    <strong>Executions:</strong> {row.executions} <br/>
                                    <strong>Executions Limit:</strong> {row.executionLimit} <br/>
                                    <strong>Average Execution Time:</strong> {row.averageExecutionTime} <br/>
                                    <strong>Next Execution:</strong> {formatDate(row.nextExecution) || 'FINISHED'} <br/>
                                </Typography>
                                <Typography style={{ flexGrow: 3 }}>
                                    <TextField
                                        label="Task Data"
                                        className={classes.dataCode}
                                        disabled
                                        multiline
                                        defaultValue={row.taskData && JSON.stringify(row.taskData)}
                                        variant="outlined"
                                    />
                                </Typography>
                            </Box>
                            <Box paddingTop={3} paddingBottom={1}>
                                <Typography variant="h6">Executions</Typography>
                                <Divider className={classes.titleDivider}/>
                                <TableContainer component={Paper} className={classes.executionsTable}>
                                    <Table stickyHeader size="small">
                                        <TableHead>
                                            <TableRow>
                                                <StyledTableHeadCell>Identifier</StyledTableHeadCell>
                                                <StyledTableHeadCell>State</StyledTableHeadCell>
                                                <StyledTableHeadCell align="center">Request Start Time</StyledTableHeadCell>
                                                <StyledTableHeadCell align="center">Request End Time</StyledTableHeadCell>
                                                <StyledTableHeadCell align="center">Execution Start Time</StyledTableHeadCell>
                                                <StyledTableHeadCell align="center">Execution Finish Time</StyledTableHeadCell>
                                                <StyledTableHeadCell/>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {executions.map((execution) => (
                                                <TableRow key={execution.id}>
                                                    <TableCell>{execution.id}</TableCell>
                                                    <TableCell><Chip color="secondary" label={execution.state} /></TableCell>
                                                    <TableCell align="center">{formatDate(execution.requestStartTime)}</TableCell>
                                                    <TableCell align="center">{formatDate(execution.requestEndTime)}</TableCell>
                                                    <TableCell align="center">{formatDate(execution.executionStartTime)}</TableCell>
                                                    <TableCell align="center">{formatDate(execution.executionFinishTime)}</TableCell>
                                                    <TableCell>
                                                        <Button
                                                            variant="contained" color="primary" size="small"
                                                            onClick={() => handleOpenLogsDialog(execution)}
                                                        >
                                                        Logs
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        {executions.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={7} align="center">
                                                    <Typography variant="body2" color="textSecondary">
                                                        No executions found.
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Box>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        )}
        {selectedExecution && (
            <LogsDialog
                execution={selectedExecution}
                open={openLogsDialog}
                onClose={() => setOpenLogsDialog(false)}
            />
        )}
        <ActiveDeactiveDialog
            scheduler={row}
            environment={environment}
            open={openActiveDeactiveDialog}
            onClose={handleCloseActiveDeactiveDialog}
        />
    </>);
}
